import { Box, Button, Container, Grid, Paper, Stack, Typography } from "@mui/material";
import { styled, alpha, useTheme } from "@mui/material/styles";
import { m } from "framer-motion";
import { useTranslation } from "react-i18next";
import BoltIcon from "@mui/icons-material/Bolt";
import SecurityIcon from "@mui/icons-material/Security";
import LayersIcon from "@mui/icons-material/Layers";
import RefreshIcon from "@mui/icons-material/Refresh";
import EmailIcon from "@mui/icons-material/Email";
import { MaintenanceIllustration } from "src/assets";
import Logo from "src/components/logo";
import Page from "src/components/Page";
import LanguagePopover from "src/layouts/shared/header/language-popover";
import { MotionContainer, varBounce, varFade } from "src/components/animate";
import { SUPPORT_MAIL, PROJECT_NAME } from "src/config";

const RootStyle = styled("div")(({ theme }) => ({
  minHeight: "100vh",
  display: "flex",
  flexDirection: "column",
  paddingTop: theme.spacing(2),
  paddingBottom: theme.spacing(6),
  background:
    theme.palette.mode === "light"
      ? `linear-gradient(135deg, ${alpha(theme.palette.primary.light, 0.08)} 0%, ${alpha(
        theme.palette.background.default,
        1
      )} 100%)`
      : `linear-gradient(135deg, ${alpha(theme.palette.background.neutral, 0.5)} 0%, ${theme.palette.background.default
      } 100%)`,
}));

const HeaderStyle = styled("header")(({ theme }) => ({
  width: "100%",
  padding: theme.spacing(2, 2),
  display: "flex",
  flexDirection: "row",
  alignItems: "center",
  justifyContent: "space-between",
  flexWrap: "wrap",
  gap: theme.spacing(1.5),
  marginBottom: theme.spacing(2),
  [theme.breakpoints.up("sm")]: {
    padding: theme.spacing(3, 4),
    marginBottom: theme.spacing(3),
  },
}));

const FeatureCard = styled(Paper)(({ theme }) => ({
  padding: theme.spacing(3),
  textAlign: "center",
  borderRadius: Number(theme.shape.borderRadius) * 2,
  backgroundColor: alpha(theme.palette.background.paper, 0.7),
  backdropFilter: "blur(8px)",
  border: `1px solid ${alpha(theme.palette.divider, 0.1)}`,
  transition: theme.transitions.create(["transform", "box-shadow"], {
    duration: theme.transitions.duration.short,
  }),
  "&:hover": {
    transform: "translateY(-4px)",
    boxShadow: theme.customShadows.z12,
  },
}));

export default function Maintenance() {
  const theme = useTheme();
  const { t } = useTranslation();

  const handleRefresh = () => {
    window.location.reload();
  };

  const supportEmail = SUPPORT_MAIL || "support@cloudmlmsoftware.ai";

  return (
    <Page title={t("maintenance.title", "Maintenance Mode")} sx={{ height: 1 }}>
      <RootStyle>
        <HeaderStyle>
          <Logo disabledLink />
          <Stack direction="row" alignItems="center" spacing={1.5}>
            <Stack
              direction="row"
              alignItems="center"
              spacing={1.25}
              sx={{
                px: 2,
                py: 0.75,
                borderRadius: "20px",
                border: (theme) => `1px solid ${alpha(theme.palette.warning.main, 0.4)}`,
                backgroundColor: (theme) => alpha(theme.palette.warning.main, 0.12),
                boxShadow: (theme) => `0 2px 8px ${alpha(theme.palette.warning.main, 0.15)}`,
              }}
            >
              <Box
                component="span"
                sx={{
                  width: 10,
                  height: 10,
                  borderRadius: "50%",
                  bgcolor: "warning.main",
                  display: "inline-block",
                  boxShadow: (theme) => `0 0 0 0 ${alpha(theme.palette.warning.main, 0.7)}`,
                  animation: "pulseDot 1.6s infinite",
                  "@keyframes pulseDot": {
                    "0%": {
                      transform: "scale(0.95)",
                      boxShadow: (theme) => `0 0 0 0 ${alpha(theme.palette.warning.main, 0.7)}`,
                    },
                    "70%": {
                      transform: "scale(1)",
                      boxShadow: (theme) => `0 0 0 8px ${alpha(theme.palette.warning.main, 0)}`,
                    },
                    "100%": {
                      transform: "scale(0.95)",
                      boxShadow: (theme) => `0 0 0 0 ${alpha(theme.palette.warning.main, 0)}`,
                    },
                  },
                }}
              />
              <Typography
                variant="subtitle2"
                sx={{
                  fontWeight: "bold",
                  color: (theme) =>
                    theme.palette.mode === "light"
                      ? theme.palette.warning.darker || "#b76e00"
                      : theme.palette.warning.light,
                  lineHeight: 1,
                }}
              >
                {t("maintenance.system_active", "System Maintenance Active")}
              </Typography>
            </Stack>
            <LanguagePopover />
          </Stack>
        </HeaderStyle>

        <Container component={MotionContainer} maxWidth="md" sx={{ textAlign: "center", my: "auto" }}>
          <m.div variants={varBounce().in}>
            <Typography
              variant="h3"
              paragraph
              sx={{
                fontWeight: "fontWeightBold",
                fontSize: { xs: "1.5rem", sm: "2rem", md: "2.5rem" },
              }}
            >
              {t("maintenance.title", "Website Currently Under Maintenance")}
            </Typography>
          </m.div>

          <m.div variants={varFade().inStyle}>
            <Typography sx={{ color: "text.secondary", maxWidth: 600, mx: "auto", mb: { xs: 2, md: 4 }, fontSize: { xs: "0.875rem", sm: "1rem" } }}>
              {t(
                "maintenance.description",
                "We are currently performing scheduled maintenance and system upgrades to enhance your overall experience. All services will resume shortly. Thank you for your patience!"
              )}
            </Typography>
          </m.div>

          <m.div variants={varBounce().in}>
            <MaintenanceIllustration sx={{ my: { xs: 2, md: 5 }, height: { xs: 160, sm: 220, md: 280 } }} />
          </m.div>

          {/* Features highlight grid */}
          <Grid container spacing={3} sx={{ mt: 1, mb: 5 }}>
            <Grid item xs={12} sm={4}>
              <m.div variants={varFade().inUp}>
                <FeatureCard elevation={0}>
                  <Box
                    sx={{
                      width: 48,
                      height: 48,
                      borderRadius: "50%",
                      mx: "auto",
                      mb: 2,
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      bgcolor: alpha(theme.palette.primary.main, 0.1),
                      color: "primary.main",
                    }}
                  >
                    <BoltIcon sx={{ width: 26, height: 26 }} />
                  </Box>
                  <Typography variant="subtitle1" sx={{ fontWeight: "bold", mb: 0.5 }}>
                    {t("maintenance.performance_upgrades", "Performance Upgrades")}
                  </Typography>
                  <Typography variant="body2" sx={{ color: "text.secondary" }}>
                    {t("maintenance.performance_upgrades_desc", "Optimizing databases & server response speed.")}
                  </Typography>
                </FeatureCard>
              </m.div>
            </Grid>

            <Grid item xs={12} sm={4}>
              <m.div variants={varFade().inUp}>
                <FeatureCard elevation={0}>
                  <Box
                    sx={{
                      width: 48,
                      height: 48,
                      borderRadius: "50%",
                      mx: "auto",
                      mb: 2,
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      bgcolor: alpha(theme.palette.info.main, 0.1),
                      color: "info.main",
                    }}
                  >
                    <SecurityIcon sx={{ width: 26, height: 26 }} />
                  </Box>
                  <Typography variant="subtitle1" sx={{ fontWeight: "bold", mb: 0.5 }}>
                    {t("maintenance.security_patching", "Security Patching")}
                  </Typography>
                  <Typography variant="body2" sx={{ color: "text.secondary" }}>
                    {t("maintenance.security_patching_desc", "Ensuring all user data remains safe & encrypted.")}
                  </Typography>
                </FeatureCard>
              </m.div>
            </Grid>

            <Grid item xs={12} sm={4}>
              <m.div variants={varFade().inUp}>
                <FeatureCard elevation={0}>
                  <Box
                    sx={{
                      width: 48,
                      height: 48,
                      borderRadius: "50%",
                      mx: "auto",
                      mb: 2,
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      bgcolor: alpha(theme.palette.success.main, 0.1),
                      color: "success.main",
                    }}
                  >
                    <LayersIcon sx={{ width: 26, height: 26 }} />
                  </Box>
                  <Typography variant="subtitle1" sx={{ fontWeight: "bold", mb: 0.5 }}>
                    {t("maintenance.new_enhancements", "New Enhancements")}
                  </Typography>
                  <Typography variant="body2" sx={{ color: "text.secondary" }}>
                    {t("maintenance.new_enhancements_desc", "Deploying exciting UI improvements & tools.")}
                  </Typography>
                </FeatureCard>
              </m.div>
            </Grid>
          </Grid>

          {/* Buttons */}
          <m.div variants={varFade().inUp}>
            <Stack direction={{ xs: "column", sm: "row" }} spacing={2} justifyContent="center">
              <Button
                variant="contained"
                size="large"
                onClick={handleRefresh}
                startIcon={<RefreshIcon />}
                sx={{ px: 4, py: 1.2, borderRadius: 2 }}
              >
                {t("maintenance.check_status", "Check System Status")}
              </Button>

              <Button
                variant="outlined"
                size="large"
                component="a"
                href={`mailto:${supportEmail}`}
                startIcon={<EmailIcon />}
                sx={{ px: 4, py: 1.2, borderRadius: 2 }}
              >
                {t("maintenance.contact_support", "Contact Support")}
              </Button>
            </Stack>
          </m.div>

          {/* Footer note */}
          <m.div variants={varFade().inUp}>
            <Typography variant="caption" sx={{ color: "text.disabled", display: "block", mt: 6 }}>
              © {new Date().getFullYear()} {PROJECT_NAME || "Cloud MLM Software"}. {t("maintenance.all_rights_reserved", "All rights reserved.")}
            </Typography>
          </m.div>
        </Container>
      </RootStyle>
    </Page>
  );
}

